import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ShieldCheck, Upload, FileText, PartyPopper, MessageCircle, Loader2, Tag, X, Zap, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Logo } from "@/components/customer/Logo";
import { api, errMsg } from "@/lib/api";
import { formatGs, formatNumber, discountPct, ORDER_STATUS } from "@/lib/format";
import { useSettings, waLink } from "@/context/SettingsContext";

const STEPS = ["Servicio", "Datos", "Pago", "Confirmado"];

const Checkout = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const s = useSettings();

  const [service, setService] = useState(null);
  const [step, setStep] = useState(0);
  const [pkg, setPkg] = useState(null);
  const [form, setForm] = useState({ instagram_username: "", customer_name: "", whatsapp: "", email: "" });
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [subscription, setSubscription] = useState(false);
  const [order, setOrder] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => {
      setService(r.data);
      const pkgs = [...(r.data.packages || [])].sort((a, b) => a.quantity - b.quantity);
      setPkg(pkgs[Math.min(1, pkgs.length - 1)] || null);
    }).catch(() => { toast.error("Servicio no encontrado"); navigate("/"); });
  }, [slug, navigate]);

  if (!service) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  }

  const packages = [...(service.packages || [])].sort((a, b) => a.quantity - b.quantity);
  const subtotal = pkg?.price || 0;
  const discount = coupon?.discount || 0;
  const total = Math.max(subtotal - discount, 0);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput, subtotal });
      setCoupon(data);
      toast.success(`Cupón aplicado: -${formatGs(data.discount)}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const igUsername = (v) => {
    let u = (v || "").trim();
    const m = u.match(/instagram\.com\/([^/?#\s]+)/i);
    if (m) u = m[1];
    return u.replace(/^@/, "").replace(/\/+$/, "");
  };
  const validUsername = (u) => /^[a-zA-Z0-9._]{1,30}$/.test(igUsername(u));

  const goDatos = () => setStep(1);

  const goPago = async () => {
    if (!validUsername(form.instagram_username)) {
      toast.error("Ingresá un usuario de Instagram válido");
      return;
    }
    if (!form.whatsapp && !form.email) {
      toast.error("Ingresá tu WhatsApp o email para poder contactarte");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        service_id: service.id,
        package_id: pkg.id,
        quantity: pkg.quantity,
        instagram_username: form.instagram_username,
        customer_name: form.customer_name,
        whatsapp: form.whatsapp,
        email: form.email,
        coupon_code: coupon?.code || null,
        subscription,
      });
      setOrder(data);
      setStep(2);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const finalize = async () => {
    if (!file) {
      toast.error("Adjuntá tu comprobante de pago");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/orders/${order.id}/comprobante`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrder({ ...order, status: data.order_status, comprobante_status: data.comprobante_status });
      setStep(3);
      toast.success("¡Pedido recibido!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-slate-200/70">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><Logo size="sm" /></Link>
          <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="checkout-close">
            <X className="h-4 w-4" /> Salir
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8">
        {/* progress */}
        <div className="flex items-center mb-8" data-testid="checkout-progress">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${i < step ? "bg-indigo-600 text-white" : i === step ? "bg-indigo-600 text-white ring-4 ring-indigo-100" : "bg-slate-200 text-slate-400"}`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className={`mt-1.5 text-[11px] font-medium ${i <= step ? "text-indigo-600" : "text-slate-400"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-2 mb-4 rounded ${i < step ? "bg-indigo-600" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* STEP 1: Servicio */}
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              <div className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100">
                <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wide">Servicio seleccionado</p>
                <h2 className="font-heading font-extrabold text-2xl text-slate-900 mt-1">{service.name}</h2>
                <p className="text-sm text-slate-500 mt-1">{service.description}</p>

                <Label className="block mt-6 mb-3 text-sm font-semibold text-slate-700">Elegí la cantidad</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {packages.map((p) => {
                    const pct = discountPct(p.price, p.original_price);
                    return (
                      <button
                        key={p.id}
                        onClick={() => setPkg(p)}
                        data-testid={`pkg-${p.quantity}`}
                        className={`relative rounded-2xl border-2 p-4 text-left transition-all active:scale-95 ${pkg?.id === p.id ? "border-indigo-600 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-300"}`}
                      >
                        {pct > 0 && (
                          <span className="absolute -top-2.5 -right-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full shadow-sm">-{pct}%</span>
                        )}
                        <p className="font-heading font-extrabold text-lg text-slate-900">{formatNumber(p.quantity)}</p>
                        {pct > 0 && <p className="text-xs text-slate-400 line-through leading-none">{formatGs(p.original_price)}</p>}
                        <p className="text-sm text-indigo-600 font-bold mt-0.5">{formatGs(p.price)}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-indigo-500" /> Inmediato</span>
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> 100% garantizado</span>
                  <span className="inline-flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5 text-pink-500" /> Reposición</span>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100 space-y-4">
                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Tag className="h-4 w-4" /> ¿Tenés un cupón?</Label>
                <div className="flex gap-2">
                  <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="CÓDIGO" className="rounded-xl" data-testid="coupon-input" />
                  <Button onClick={applyCoupon} variant="outline" className="rounded-xl border-slate-300" data-testid="coupon-apply">Aplicar</Button>
                </div>
                {coupon && <p className="text-sm text-emerald-600 font-medium">✓ Cupón {coupon.code}: -{formatGs(coupon.discount)}</p>}

                <label className="flex items-start gap-3 pt-2 cursor-pointer">
                  <Checkbox checked={subscription} onCheckedChange={setSubscription} data-testid="subscription-checkbox" className="mt-0.5" />
                  <span className="text-sm text-slate-600">¿Querés mantener este servicio todos los meses? <span className="text-slate-400">(activar suscripción mensual, opcional)</span></span>
                </label>
              </div>

              <div className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{formatNumber(pkg?.quantity)} · {service.name.split(" ")[0]}</p>
                  <p className="font-heading font-extrabold text-3xl text-slate-900">{formatGs(total)}</p>
                  {discount > 0 && <p className="text-xs text-slate-400 line-through">{formatGs(subtotal)}</p>}
                </div>
                <Button onClick={goDatos} data-testid="step1-continue" className="rounded-full h-12 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold active:scale-95 transition-all">
                  Continuar <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Datos */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              <div className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100 space-y-5">
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Usuario de Instagram *</Label>
                  <Input value={form.instagram_username} onChange={(e) => setForm({ ...form, instagram_username: e.target.value })} placeholder="@tuusuario o link del perfil" className="rounded-xl mt-2 h-12" data-testid="input-username" />
                  <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Podés pegar tu @usuario o el link de tu perfil. Nunca te pediremos tu contraseña.</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Tu nombre</Label>
                  <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nombre y apellido" className="rounded-xl mt-2 h-12" data-testid="input-name" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">WhatsApp *</Label>
                  <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+595 9XX XXXXXX" className="rounded-xl mt-2 h-12" data-testid="input-whatsapp" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Email (opcional)</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tucorreo@email.com" className="rounded-xl mt-2 h-12" data-testid="input-email" />
                </div>
              </div>

              {/* resumen */}
              <div className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100">
                <p className="font-heading font-bold text-slate-900 mb-3">Tu pedido</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Servicio</span><span className="font-medium text-slate-800">{service.name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Usuario</span><span className="font-medium text-slate-800">@{igUsername(form.instagram_username) || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Cantidad</span><span className="font-medium text-slate-800">{formatNumber(pkg?.quantity)}</span></div>
                  {discount > 0 && <div className="flex justify-between"><span className="text-slate-500">Descuento</span><span className="font-medium text-emerald-600">-{formatGs(discount)}</span></div>}
                  <div className="flex justify-between pt-2 border-t border-slate-100"><span className="font-semibold text-slate-900">Total</span><span className="font-heading font-extrabold text-lg text-indigo-600">{formatGs(total)}</span></div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setStep(0)} variant="outline" className="rounded-full h-12 px-5 border-slate-300" data-testid="step2-back"><ArrowLeft className="h-4 w-4" /></Button>
                <Button onClick={goPago} disabled={loading} data-testid="step2-continue" className="flex-1 rounded-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold active:scale-95 transition-all">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Continuar al pago <ArrowRight className="ml-1 h-4 w-4" /></>}
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Pago */}
          {step === 2 && order && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              <div className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100">
                <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wide">Pedido #{order.order_number}</p>
                <h2 className="font-heading font-extrabold text-2xl text-slate-900 mt-1">Realizá el pago</h2>
                <p className="text-sm text-slate-500 mt-1">Transferí el total y adjuntá tu comprobante para confirmar.</p>

                <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Total a pagar</span><span className="font-heading font-extrabold text-xl text-indigo-600">{formatGs(order.total)}</span></div>
                  {s.alias && <div className="flex justify-between"><span className="text-slate-500">Alias</span><span className="font-medium text-slate-800">{s.alias}</span></div>}
                  {s.bank_details && <div className="pt-1"><span className="text-slate-500 block mb-1">Datos bancarios</span><span className="font-medium text-slate-800 text-xs leading-relaxed">{s.bank_details}</span></div>}
                </div>
              </div>

              <div className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100">
                <Label className="text-sm font-semibold text-slate-700">Adjuntar comprobante de pago *</Label>
                <p className="text-xs text-slate-400 mt-1 mb-3">Formatos: JPG, PNG o PDF.</p>
                <label className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 cursor-pointer transition-colors ${file ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-indigo-400 bg-slate-50"}`} data-testid="comprobante-dropzone">
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="comprobante-input" />
                  {file ? (
                    <><FileText className="h-8 w-8 text-emerald-600 mb-2" /><span className="text-sm font-medium text-emerald-700">{file.name}</span><span className="text-xs text-slate-400 mt-1">Tocá para cambiar</span></>
                  ) : (
                    <><Upload className="h-8 w-8 text-slate-400 mb-2" /><span className="text-sm font-medium text-slate-600">Tocá para subir tu comprobante</span></>
                  )}
                </label>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setStep(1)} variant="outline" className="rounded-full h-12 px-5 border-slate-300" data-testid="step3-back"><ArrowLeft className="h-4 w-4" /></Button>
                <Button onClick={finalize} disabled={loading} data-testid="step3-finalize" className="flex-1 rounded-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold active:scale-95 transition-all">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Finalizar pedido"}
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Confirmado */}
          {step === 3 && order && (
            <motion.div key="s3" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6">
              <div className="bg-white rounded-3xl p-8 soft-shadow border border-slate-100 text-center">
                <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <PartyPopper className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="font-heading font-extrabold text-2xl text-slate-900">¡Pedido recibido! 🎉</h2>
                <p className="text-slate-500 mt-1">Tu pedido fue registrado correctamente.</p>

                <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-100 p-5 text-left space-y-2.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Pedido</span><span className="font-heading font-bold text-slate-900">#{order.order_number}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Servicio</span><span className="font-medium text-slate-800">{service.name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Usuario</span><span className="font-medium text-slate-800">@{order.instagram_username}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Cantidad</span><span className="font-medium text-slate-800">{formatNumber(order.quantity)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Valor</span><span className="font-medium text-slate-800">{formatGs(order.total)}</span></div>
                  <div className="flex justify-between items-center pt-1"><span className="text-slate-500">Estado</span><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${ORDER_STATUS[order.status]?.color}`}>{ORDER_STATUS[order.status]?.label}</span></div>
                </div>
              </div>

              <a href={waLink(s.whatsapp, `Hola, hice el pedido #${order.order_number} en Impulso Visual y necesito ayuda.`)} target="_blank" rel="noreferrer" data-testid="confirm-whatsapp">
                <Button className="w-full rounded-full h-12 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold active:scale-95 transition-all">
                  <MessageCircle className="mr-2 h-5 w-5" /> Enviar comprobante por WhatsApp
                </Button>
              </a>
              <div className="flex gap-3">
                <Button onClick={() => navigate("/consultar-pedido")} variant="outline" className="flex-1 rounded-full h-12 border-slate-300" data-testid="confirm-track">Consultar pedido</Button>
                <Button onClick={() => navigate("/")} variant="ghost" className="flex-1 rounded-full h-12" data-testid="confirm-home">Volver al inicio</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Checkout;
